import { Css, FormSectionLayout, ResponsiveGrid, ResponsiveGridItem } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ReactNode } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { BaseChangeEventFormInput } from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { ApplyConfigurationCard } from "src/routes/projects/change-events/guided/workflows/components/ApplyConfigurationCard";
import { ApplyConfigurationCard_CheckoutConfigFragment } from "src/routes/projects/change-events/guided/workflows/components/ApplyConfigurationCard.generated";
import { ProjectParams } from "src/routes/routesDef";
import { fail, queryResult } from "src/utils";
import { useVersionUpdateStepQuery } from "./1.VersionUpdateStep.generated";

type VersionUpdateStepProps = { formState: ObjectState<BaseChangeEventFormInput> };

/**
 * Shows the project's current configuration next to the one it is being trued up to. The draft is created before the
 * wizard mounts, so the backend has already composed the `target` from the latest promoted Product Offering version.
 */
export function VersionUpdateStep(props: VersionUpdateStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const changeEventId = formState.id.value ?? fail("Configuration True-Up draft was not created");
  const query = useVersionUpdateStepQuery({ variables: { changeEventId, structureId: projectId } });
  return queryResult(query, ({ changeEvent }) => {
    // The backend always sets both on a true-up: `original` from the project, `target` synced to the new version
    const { original, target } = changeEvent.configChange ?? fail("Change event has no config change");
    return (
      <VersionUpdateStepView
        currentConfig={original ?? fail("Config change has no original configuration")}
        newConfig={target ?? fail("Config change has no target configuration")}
      />
    );
  });
}

type VersionUpdateStepViewProps = {
  currentConfig: ApplyConfigurationCard_CheckoutConfigFragment;
  newConfig: ApplyConfigurationCard_CheckoutConfigFragment;
};

function VersionUpdateStepView(props: VersionUpdateStepViewProps) {
  const { currentConfig, newConfig } = props;
  return (
    <FormSectionLayout
      title="Product Offering Version Update"
      description="This project is being updated to a new version of the Product Offering. We'll help you review any discontinued options."
      sections={[
        {
          title: "",
          fields: (
            <ResponsiveGrid columns={2} minColumnWidth={280} gap={24}>
              <VersionPanel title="Current Version">
                <ApplyConfigurationCard checkoutConfig={currentConfig} />
              </VersionPanel>
              <VersionPanel title="New Version">
                <ApplyConfigurationCard checkoutConfig={newConfig} />
              </VersionPanel>
            </ResponsiveGrid>
          ),
        },
      ]}
    />
  );
}

function VersionPanel(props: { title: string; children: ReactNode }) {
  const { title, children } = props;
  return (
    <ResponsiveGridItem colSpan={1}>
      <div css={Css.df.fdc.gap2.$}>
        <span css={Css.mdSb.$}>{title}</span>
        {children}
      </div>
    </ResponsiveGridItem>
  );
}
